'use client';

import {
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Stack,
  Typography,
} from '@mui/material';
import { Switch } from '@/primitives';
import AnimalIdentMultiSelect from '../../mating/AnimalIdentMultiSelect';
import FilterConditionInput from './FilterConditionInput';
import MemberIdentMultiSelect from '../../mating/MemberIdentMultiSelect';
import CalvingYearChipInput from './CalvingYearChipInput';
import { IDENT_CHIP_PLACEHOLDER } from '../quickSearchCopy';
import { FieldOption, FilterInput, OperatorOption } from '../../constants';
import {
  AnimalSearchFieldLookup,
  getOptionsByFieldAndSearchType,
} from '../constants';
import { RangeCompositeValue } from '../rangeValue';
import { pillSelectSx, pillTextFieldSx } from './filterPillStyles';

interface FilterPillValueSegmentProps {
  index: number;
  field: string;
  operator?: string;
  searchType: string;
  filterValue: string;
  inputType?: string;
  isSaleCountField: boolean;
  isLookupField: boolean;
  useEbvSpecializedInputs: boolean;
  isQuickSearchField: boolean;
  isAnimalQuickSearchField: boolean;
  isSexField: boolean;
  isDateField: boolean;
  isTattooField: boolean;
  isCalvingYearField: boolean;
  isBetweenOperator: boolean;
  ebvInputs: RangeCompositeValue;
  dateRangeInputs: RangeCompositeValue;
  rangeInputs: RangeCompositeValue;
  animalIdentChipValue: string;
  lookupChoices: AnimalSearchFieldLookup[];
  updateFilter: (index: number, filter: Partial<FilterInput>) => void;
  debounceUpdateFilter: (index: number, filter: Partial<FilterInput>) => void;
  setFilterValue: (value: string) => void;
  handleEbvInputChange: (
    inputKey: keyof RangeCompositeValue,
    inputValue: string,
  ) => void;
  handleAnimalIdentChipChange: (next: string) => void;
  handleSexValueChange: (value: string) => void;
  handleDateRangeChange: (
    inputKey: keyof RangeCompositeValue,
    inputValue: string,
  ) => void;
  handleTattooValueChange: (value: string) => void;
  handleCalvingYearValueChange: (value: string) => void;
  handleRangeInputChange: (
    inputKey: keyof RangeCompositeValue,
    inputValue: string,
  ) => void;
}

export default function FilterPillValueSegment({
  index,
  field,
  operator,
  searchType,
  filterValue,
  inputType,
  isSaleCountField,
  isLookupField,
  useEbvSpecializedInputs,
  isQuickSearchField,
  isAnimalQuickSearchField,
  isSexField,
  isDateField,
  isTattooField,
  isCalvingYearField,
  isBetweenOperator,
  ebvInputs,
  dateRangeInputs,
  rangeInputs,
  animalIdentChipValue,
  lookupChoices,
  updateFilter,
  debounceUpdateFilter,
  setFilterValue,
  handleEbvInputChange,
  handleAnimalIdentChipChange,
  handleSexValueChange,
  handleDateRangeChange,
  handleTattooValueChange,
  handleCalvingYearValueChange,
  handleRangeInputChange,
}: FilterPillValueSegmentProps) {
  if (isSaleCountField) {
    const onSale = operator !== OperatorOption.equals.value;
    return (
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: 'center',
          pr: 1,
          whiteSpace: 'nowrap',
        }}
      >
        <Switch
          size="small"
          checked={onSale}
          onChange={(e) =>
            updateFilter(index, {
              operator: e.target.checked
                ? OperatorOption.greaterThan.value
                : OperatorOption.equals.value,
              value: '0',
            })
          }
        />
        <Typography
          variant="labelmedium"
          sx={{ fontSize: '13px', color: 'text.secondary' }}
        >
          {onSale ? 'On sale' : 'Not on sale'}
        </Typography>
      </Stack>
    );
  }
  if (isLookupField) {
    // Description dropdown for society-defined lookup pills (Member Zone,
    // Select if, Herd Completeness…). The pill stores `value = lookup.code`;
    // the underlying real filter is materialised at GraphQL-build time from
    // the lookup's `i6LookupString`.
    const handleLookupChange = (e: SelectChangeEvent<unknown>) => {
      const code = e.target.value as string;
      setFilterValue(code);
      updateFilter(index, { operator: 'LOOKUP', value: code });
    };
    return (
      <Select
        variant="standard"
        disableUnderline
        displayEmpty
        value={filterValue}
        onChange={handleLookupChange}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography
                variant="labelmedium"
                sx={{ color: 'inherit', opacity: 0.5, fontSize: '13px' }}
              >
                Select value…
              </Typography>
            );
          }
          const found = lookupChoices.find((l) => l.code === selected);
          return (
            <Typography
              variant="labelmedium"
              sx={{ fontSize: '13px', whiteSpace: 'nowrap' }}
            >
              {found?.description ?? String(selected)}
            </Typography>
          );
        }}
        sx={{
          ...pillSelectSx,
          minWidth: '120px',
          width: '100%',
        }}
      >
        <MenuItem value="">
          <em>Clear</em>
        </MenuItem>
        {lookupChoices.map((lookup) => (
          <MenuItem key={lookup.code} value={lookup.code}>
            <Typography variant="labelmedium" sx={{ fontSize: '13px' }}>
              {lookup.description}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    );
  }

  if (useEbvSpecializedInputs) {
    return (
      <Stack
        direction="row"
        spacing={0}
        sx={{ alignItems: 'center', height: '100%' }}
      >
        <TextField
          placeholder={`Min`}
          type="number"
          size="small"
          variant="standard"
          value={ebvInputs.min || ''}
          onChange={(e) => handleEbvInputChange('min', e.target.value)}
          sx={{
            minWidth: '48px',
            width: `${Math.max(3, (ebvInputs.min || '').length + 1)}ch`,
          }}
          slotProps={{
            input: { disableUnderline: true },

            htmlInput: {
              min: 0,
              style: {
                fontSize: '13px',
                padding: '0 6px',
                textAlign: 'center',
              },
            },
          }}
        />
        <Typography
          sx={{ fontSize: '11px', color: 'text.secondary', px: 0.25 }}
        >
          –
        </Typography>
        <TextField
          placeholder={`Max`}
          type="number"
          size="small"
          variant="standard"
          value={ebvInputs.max || ''}
          onChange={(e) => handleEbvInputChange('max', e.target.value)}
          sx={{
            minWidth: '48px',
            width: `${Math.max(3, (ebvInputs.max || '').length + 1)}ch`,
          }}
          slotProps={{
            input: { disableUnderline: true },

            htmlInput: {
              min: 0,
              style: {
                fontSize: '13px',
                padding: '0 6px',
                textAlign: 'center',
              },
            },
          }}
        />
      </Stack>
    );
  }

  if (isQuickSearchField) {
    const qsSx = {
      '& .MuiInputBase-root': {
        height: '34px',
        fontSize: '16px', // iOS Safari auto-zooms inputs with font-size < 16px and doesn't zoom back out
        '& fieldset': { border: 'none' },
      },
      '& .MuiInputBase-input': { px: '8px', py: 0, fontSize: '16px' }, // iOS Safari auto-zooms inputs with font-size < 16px and doesn't zoom back out
      '& .MuiAutocomplete-input': {
        width: 'auto !important',
        minWidth: '60px !important',
        textOverflow: 'clip !important',
      },
    };
    if (isAnimalQuickSearchField) {
      return (
        <AnimalIdentMultiSelect
          value={animalIdentChipValue}
          onChange={handleAnimalIdentChipChange}
          placeholder="Search..."
          showExplicitWildcardOptions
          size="small"
          sexFilter={(() => {
            if (field === FieldOption.sire.value) return 'M';
            if (field === FieldOption.dam.value) return 'F';
            return undefined;
          })()}
          sx={{ ...qsSx, minWidth: '100px', width: '100%' }}
        />
      );
    }
    return (
      <MemberIdentMultiSelect
        value={animalIdentChipValue}
        onChange={handleAnimalIdentChipChange}
        placeholder={IDENT_CHIP_PLACEHOLDER}
        showExplicitWildcardOptions
        sx={{ ...qsSx, minWidth: '100px', width: '100%' }}
      />
    );
  }

  if (isSexField) {
    return (
      <FilterConditionInput
        disabled={false}
        label=""
        options={getOptionsByFieldAndSearchType(field, searchType)}
        multiple={true}
        type={inputType || 'text'}
        value={filterValue}
        selectSx={{
          ...pillSelectSx,
          minWidth: '60px',
          width: 'auto',
        }}
        textFieldSx={pillTextFieldSx}
        onChange={(v) => {
          setFilterValue(v);
          handleSexValueChange(v);
        }}
      />
    );
  }

  if (isDateField) {
    return (
      <Stack
        direction="row"
        spacing={0}
        sx={{ alignItems: 'center', height: '100%' }}
      >
        <TextField
          placeholder="From"
          type="date"
          size="small"
          variant="standard"
          value={dateRangeInputs.min || ''}
          onChange={(e) => handleDateRangeChange('min', e.target.value)}
          sx={{ minWidth: '110px', width: 'auto' }}
          slotProps={{
            input: { disableUnderline: true },
            htmlInput: { style: { fontSize: '16px', padding: '0 6px' } },
          }}
        />
        <Typography
          sx={{ fontSize: '11px', color: 'text.secondary', px: 0.25 }}
        >
          –
        </Typography>
        <TextField
          placeholder="To"
          type="date"
          size="small"
          variant="standard"
          value={dateRangeInputs.max || ''}
          onChange={(e) => handleDateRangeChange('max', e.target.value)}
          sx={{ minWidth: '110px', width: 'auto' }}
          slotProps={{
            input: { disableUnderline: true },
            htmlInput: { style: { fontSize: '16px', padding: '0 6px' } },
          }}
        />
      </Stack>
    );
  }

  if (isTattooField) {
    return (
      <TextField
        placeholder="Tattoo"
        size="small"
        variant="standard"
        value={filterValue}
        onChange={(e) => handleTattooValueChange(e.target.value)}
        sx={{
          minWidth: '56px',
          width: '100%',
        }}
        slotProps={{
          input: { disableUnderline: true },
          htmlInput: { style: { fontSize: '16px', padding: '0 8px' } },
        }}
      />
    );
  }

  if (isCalvingYearField) {
    return (
      <CalvingYearChipInput
        value={filterValue}
        onChange={handleCalvingYearValueChange}
        sx={{
          minWidth: '80px',
          width: 'auto',
          '& .MuiOutlinedInput-root': {
            minHeight: '34px',
            borderRadius: 0,
            '& fieldset': { border: 'none' },
            '& .MuiOutlinedInput-input': { px: '8px', fontSize: '16px' }, // iOS Safari auto-zooms inputs with font-size < 16px and doesn't zoom back out
          },
          '& .MuiChip-root': { height: '22px', fontSize: '12px' },
        }}
      />
    );
  }

  // Default: 3-part pill value segment
  if (isBetweenOperator) {
    return (
      <Stack
        direction="row"
        spacing={0}
        sx={{ alignItems: 'center', height: '100%' }}
      >
        <TextField
          placeholder="Min"
          type={inputType || 'text'}
          size="small"
          variant="standard"
          value={rangeInputs.min || ''}
          onChange={(e) => handleRangeInputChange('min', e.target.value)}
          sx={{
            minWidth: '48px',
            width: `${Math.max(3, (rangeInputs.min || '').length + 1)}ch`,
          }}
          slotProps={{
            input: { disableUnderline: true },

            htmlInput: {
              style: {
                fontSize: '13px',
                padding: '0 6px',
                textAlign: 'center',
              },
            },
          }}
        />
        <Typography
          sx={{ fontSize: '11px', color: 'text.secondary', px: 0.25 }}
        >
          –
        </Typography>
        <TextField
          placeholder="Max"
          type={inputType || 'text'}
          size="small"
          variant="standard"
          value={rangeInputs.max || ''}
          onChange={(e) => handleRangeInputChange('max', e.target.value)}
          sx={{
            minWidth: '48px',
            width: `${Math.max(3, (rangeInputs.max || '').length + 1)}ch`,
          }}
          slotProps={{
            input: { disableUnderline: true },

            htmlInput: {
              style: {
                fontSize: '13px',
                padding: '0 6px',
                textAlign: 'center',
              },
            },
          }}
        />
      </Stack>
    );
  }

  return (
    <FilterConditionInput
      disabled={operator === OperatorOption.isNotEmpty.value}
      label=""
      options={getOptionsByFieldAndSearchType(field, searchType)}
      multiple={false}
      type={inputType || 'text'}
      value={filterValue}
      selectSx={{
        ...pillSelectSx,
        minWidth: '60px',
        width: '100%',
      }}
      textFieldSx={{
        ...pillTextFieldSx,
        minWidth: '56px',
        width: '100%',
      }}
      onChange={(v) => {
        setFilterValue(v);
        debounceUpdateFilter(index, { value: v });
      }}
    />
  );
}
